import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UIIconComponent, UiButtonComponent, UiInputComponent } from '@design-system';
import { IconName, IconNames } from '@icon/assets';
import { TickType } from '@setting';
import { OrderFormFacade } from '../../data-access/order-form.facade';
import { OrderQuantityInputComponent } from '../order-quantity-input/order-quantity-input.component';
import { KeyboardDirective, KeyboardStepMode } from '../../../../components/keyboard';
import { formatCompactNumber, formatWithSeparator } from '../../tools/order-calculator.tools';

/** Temporary demo host for the order calculator (EAT-11201 / EAT-10585). */
@Component({
	selector: 'app-order-form-fields',
	templateUrl: './order-form-fields.component.html',
	imports: [
		FormsModule,
		UiInputComponent,
		OrderQuantityInputComponent,
		UIIconComponent,
		UiButtonComponent,
		KeyboardDirective
	]
})
export class OrderFormFieldsComponent {
	private readonly orderFormFacade = inject(OrderFormFacade);

	readonly minPrice = computed(() => {
		const { price } = this.orderFormFacade.validRanges();
		return price.max > 0 ? price.min : undefined;
	});
	readonly maxPrice = computed(() => {
		const { price } = this.orderFormFacade.validRanges();
		return price.max > 0 ? price.max : undefined;
	});
	readonly priceLocked = computed(() => this.orderFormFacade.priceLocked());

	/** Bound chips are compacted (30.79M) so the buttons stay narrow. */
	readonly minPriceLabel = computed(() => this.compactBound(this.minPrice()));
	readonly maxPriceLabel = computed(() => this.compactBound(this.maxPrice()));

	readonly priceError = computed(() => {
		const value = this.orderFormFacade.orderFormModel().price;
		// An empty field is only flagged once the calculator is waiting on a price.
		if (value === null || value === undefined) {
			return this.orderFormFacade.priceMissing();
		}
		return this.orderFormFacade.form.price().invalid();
	});

	readonly priceFieldClasses = computed(() => {
		const base =
			'w-full [&_.ui-input-shell]:min-h-11! [&_.ui-input-shell]:bg-gray-000! [&_.ui-input-shell]:[--ui-input-label-mask-color:var(--color-gray-000)]! [&_input]:[direction:ltr]';

		if (this.priceError()) {
			return `${base} [&_input]:text-red-500!`;
		}
		if (this.priceLocked()) {
			return `${base} [&_input]:text-disable-300! [&_.ui-input-shell]:border-disable-200!`;
		}
		return base;
	});

	readonly lockPriceIcon = computed<IconNames>(() => (this.priceLocked() ? IconName.LockFill : IconName.LockOpen));

	readonly priceStepMode = computed(() =>
		this.orderFormFacade.orderTickType() === TickType.Percent ? KeyboardStepMode.Percent : KeyboardStepMode.Amount
	);

	readonly priceStepValue = computed(() => {
		const tick = this.orderFormFacade.orderTick();
		const tickSize = this.orderFormFacade.tickSize();

		if (Number.isFinite(tick)) {
			return tick;
		}
		return this.orderFormFacade.orderTickType() === TickType.Amount ? tickSize : 0;
	});

	readonly formattedPrice = computed(() => {
		const price = this.orderFormFacade.orderFormModel().price;
		// Cleared field → empty; explicit 0 (e.g. keyboard −1) → "0"
		if (price === null || price === undefined) {
			return '';
		}
		return formatWithSeparator(price);
	});

	onPriceChange(raw: string | number): void {
		if (this.priceLocked()) {
			return;
		}

		const next = this.parsePriceInput(raw);
		const current = this.orderFormFacade.orderFormModel().price;
		if (current === next) {
			return;
		}
		this.orderFormFacade.setFormData({ price: next });
	}

	setMaxPrice(): void {
		this.fillPrice(this.maxPrice());
	}

	setMinPrice(): void {
		this.fillPrice(this.minPrice());
	}

	private compactBound(bound: number | undefined): string {
		return bound === undefined ? '' : formatCompactNumber(bound);
	}

	private fillPrice(bound: number | undefined): void {
		if (bound === undefined) {
			return;
		}
		if (this.priceLocked()) {
			this.orderFormFacade.setPriceLock(false);
		}
		this.onPriceChange(bound);
	}

	toggleLock(): void {
		this.orderFormFacade.togglePriceLock();
	}

	/** Empty clear → null; otherwise keep the parsed number (including 0). */
	private parsePriceInput(raw: string | number): number | null {
		if (raw === '' || raw === null || raw === undefined) {
			return null;
		}
		const parsed = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
		if (!Number.isFinite(parsed)) {
			return null;
		}
		return Math.max(0, parsed);
	}
}
